<template>
  <div class="testimonial-section">
    <h2 class="section-title" :class="isArabic ? 'arabic-text' : ''">
      {{ $t('landing_page_testimonials_title') }}
    </h2>

    <div class="tw-mt-10 reel-cards">
      <div class="auto-slider-container-cards" ref="slider">
        <div
          class="auto-slider-row-cards"
          v-for="(rowItems, rowIndex) in splitTestimonials"
          :key="`row-${rowIndex}`"
        >
        <div
          v-for="(item, index) in rowItems"
          :key="`testimonial-${rowIndex}-${index}`"
          class="testimonial-card card-content"
        >
          <p class="testimonial-text" :class="isArabic ? 'arabic-text' : ''">{{ $t(item.title) }}</p>
          <div class="user-info">
            <img :src="getTestimonialImage(item.name)" alt="User Avatar" class="avatar" />
            <div>
              <p class="user-name">{{ $t(item.name) }}</p>
              <p class="user-detail">{{ $t(item.description) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
    <div class="controls-row">
      <div class="dots">
        <span
          v-for="(_, index) in totalPages"
          :key="`dot-${index}`"
          class="dot"
          :class="{ active: currentIndex === index }"
          @click="goToSlide(index)"
        ></span>
      </div>
      <div class="arrows-container">
        <button class="arrow" @click="scrollLeft">
          <img src="../assets/images/CaretLeft.svg?url" alt="left" class="image-color" />
        </button>
        <button class="arrow" @click="scrollRight">
          <img src="../assets/images/CaretRight.svg" alt="right" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import TestimonialMixin from "../mixins/testimonials";

export default {
  name: "TestimonialCards",
  mixins: [TestimonialMixin],

  data() {
    return {
      currentIndex: 0,
      isDragging: false,
      startX: 0,
      scrollStartLeft: 0,
      cardWidth: 300,
      testimonials: [
        {
          title: "Landing_page_testimonials_1_text",
          name: "Landing_page_testimonials_1_name",
          description: "Landing_page_testimonials_1_subtitle",
        },
        {
          title: "Landing_page_testimonials_2_text",
          name: "Landing_page_testimonials_2_name",
          description: "Landing_page_testimonials_2_subtitle",
        },
        {
          title: "Landing_page_testimonials_3_text",
          name: "Landing_page_testimonials_3_name",
          description: "Landing_page_testimonials_3_subtitle",
        },
        {
          title: "Landing_page_testimonials_4_text",
          name: "Landing_page_testimonials_4_name",
          description: "Landing_page_testimonials_4_subtitle",
        },
        {
          title: "Landing_page_testimonials_5_text",
          name: "Landing_page_testimonials_5_name",
          description: "Landing_page_testimonials_5_subtitle",
        },
        {
          title: "Landing_page_testimonials_6_text",
          name: "Landing_page_testimonials_6_name",
          description: "Landing_page_testimonials_6_subtitle",
        },
        {
          title: "Landing_page_testimonials_7_text",
          name: "Landing_page_testimonials_7_name",
          description: "Landing_page_testimonials_7_subtitle",
        },
        {
          title: "Landing_page_testimonials_8_text",
          name: "Landing_page_testimonials_8_name",
          description: "Landing_page_testimonials_8_subtitle",
        },
        {
          title: "Landing_page_testimonials_9_text",
          name: "Landing_page_testimonials_9_name",
          description: "Landing_page_testimonials_9_subtitle",
        },
        {
          title: "Landing_page_testimonials_10_text",
          name: "Landing_page_testimonials_10_name",
          description: "Landing_page_testimonials_10_subtitle",
        },
      ],
    };
  },

  computed: {
    visibleCards() {
      return Math.floor((this.$refs.slider?.offsetWidth || 1) / this.cardWidth);
    },
    totalPages() {
      return Math.ceil(this.testimonials.length);
    },
    isArabic() {
      return this.$i18n.locale === "ar";
    },
    splitTestimonials() {
      if (window.innerWidth >= 1024) {
        const firstRow = this.testimonials.slice(0, Math.ceil(this.testimonials.length / 2));
        const secondRow = this.testimonials.slice(Math.ceil(this.testimonials.length / 2));
        return [firstRow, secondRow];
      } else {
        // On small screens, treat all as one row
        return [this.testimonials];
      }
    },
  },

  methods: {
    scrollToIndex() {
      this.$refs.slider.scrollTo({
        left: this.currentIndex * this.cardWidth,
        behavior: "smooth",
      });
    },

    scrollLeft() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.scrollToIndex();
      }
    },

    scrollRight() {
      if (this.currentIndex < this.totalPages - 1) {
        this.currentIndex++;
        this.scrollToIndex();
      }
    },

    goToSlide(index) {
      if (this.currentIndex !== index) {
        this.currentIndex = index;
        this.scrollToIndex();
      }
    },

    handleScroll() {
      const index = Math.round(this.$refs.slider.scrollLeft / this.cardWidth);
      if (this.currentIndex !== index) this.currentIndex = index;
    },

    startDrag(e) {
      this.isDragging = true;
      const pageX = e.pageX ?? e.touches[0].pageX;
      this.startX = pageX - this.$refs.slider.offsetLeft;
      this.scrollStartLeft = this.$refs.slider.scrollLeft;
      this.$refs.slider.classList.add("dragging");
    },

    dragMove(e) {
      if (!this.isDragging) return;
      e.preventDefault();
      const pageX = e.pageX ?? e.touches[0].pageX;
      const x = pageX - this.$refs.slider.offsetLeft;
      this.$refs.slider.scrollLeft = this.scrollStartLeft - (x - this.startX);
    },

    endDrag() {
      this.isDragging = false;
      this.$refs.slider.classList.remove("dragging");
    },

    addListeners() {
      const el = this.$refs.slider;
      el.addEventListener("mousedown", this.startDrag);
      el.addEventListener("mousemove", this.dragMove);
      el.addEventListener("mouseup", this.endDrag);
      el.addEventListener("mouseleave", this.endDrag);
      el.addEventListener("touchstart", this.startDrag, { passive: true });
      el.addEventListener("touchmove", this.dragMove, { passive: false });
      el.addEventListener("touchend", this.endDrag);
      el.addEventListener("scroll", this.handleScroll);
    },

    removeListeners() {
      const el = this.$refs.slider;
      el.removeEventListener("mousedown", this.startDrag);
      el.removeEventListener("mousemove", this.dragMove);
      el.removeEventListener("mouseup", this.endDrag);
      el.removeEventListener("mouseleave", this.endDrag);
      el.removeEventListener("touchstart", this.startDrag);
      el.removeEventListener("touchmove", this.dragMove);
      el.removeEventListener("touchend", this.endDrag);
      el.removeEventListener("scroll", this.handleScroll);
    },
  },

  mounted() {
    this.$nextTick(this.addListeners);
  },

  beforeDestroy() {
    this.removeListeners();
  },
};
</script>

<style lang="scss" scoped>
  .reel-cards {
    width: 100%;
    position: relative;
  }
  
  .auto-slider-container-cards {
    overflow-x: auto;
    white-space: nowrap;
    scroll-behavior: smooth;
    overflow: hidden;
    display: flex;
    padding-block: 10px;
  }
  .auto-slider-row-cards {
    display: inline-flex;
    gap: 20px;
  }
  @keyframes row-anim-reverse {
    0% {
      transform: translate(0, 0);
    }
    100% {
      transform: translate(50%, 0);
    }
  }
.testimonial-section {
  font-family: "Nunito-Regular", sans-serif;
  color: #020076;
  text-align: center;
  padding-block: 5%;

  @media (orientation: portrait) {
    padding-block: 8%;
  }
}

.section-title {
  font-size: 2.5rem;
  font-family: "Nunito-ExtraBold";
  display: flex;
  flex-direction: row;
  justify-content: center;
  line-height: 1;
  padding-inline: 3%;
  
  @media (orientation: portrait) {
      font-size: 1.5rem;
  }
}

.testimonial-text {
  font-size: 14px;
  line-height: 1.1;
  text-align: left;
  word-break: break-word;
  white-space: normal;
  min-height: 60%;
}

.user-info {
  display: flex;
  align-items: center;
}

.user-name {
  font-family: "Nunito-ExtraBold";
  margin: 0;
  font-size: 16px;
  color: #020076;
}

.dot {
  height: 10px;
  width: 10px;
  margin: 0 5px;
  background-color: #020076;
  border-radius: 50%;
  display: inline-block;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.dot.active {
  background-color: #FFA726;
}

.arrow {
  width: 38px;
  height: 38px;
  background-color: #f6f8fb;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.10s ease;
}
.testimonial-card {
  background-color: #F2F2F6;
  border-radius: 20px;
  padding: 20px;
  max-width: 360px;
  font-family: 'Nunito-Regular';
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 360px;
  @media (orientation: portrait) {
    min-width: 300px;
    max-width: 300px;
    
  }
}
.auto-slider-container-cards .card-content {
    @media (min-width: 1024px) {
      min-width: 400px;
    }
  }
  .auto-slider-container-cards  {
    @media (min-width: 1024px) {
      flex-wrap: wrap;
    }
  }
.testimonial-text {
  font-size: 14px;
  color: #020076;
  line-height: 1.5;
  text-align: start;
}


.avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-right: 12px;
  object-fit: contain;
}

.user-name {
  font-family: "Nunito-ExtraBold";
  margin: 0;
  font-size: 16px;
  color: #020076;
  text-align: left;
}

.user-detail {
  font-size: 12px;
  color: #640E4B;
  margin: 0;    
  text-wrap: initial;
  text-align: left; 
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
  
  @media screen and (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    white-space: normal !important;
    word-break: break-word !important;
    overflow-wrap: break-word !important;
    max-width: 100% !important;
  }
}
.controls-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 20px;
  padding-inline: 15px;

  @media (orientation: portrait) {
    justify-content: space-between;
    
  }
}

.dots {
  display: none;
  @media (orientation: portrait) {
    display: flex;
    justify-content: center;
    flex: 1;
  }
}

.arrows-container {
  display: flex;
  gap: 10px;

  @media (orientation: portrait) {
    display: none;
  }
}
.image-color {
  filter: brightness(0) saturate(100%) invert(9%) sepia(100%) saturate(1000%) hue-rotate(210deg);
}
.auto-slider-container-cards {
  cursor: grab;
  user-select: none;
}

.auto-slider-container-cards .dragging {
  cursor: grabbing;
}
.auto-slider-container-cards {
  -webkit-overflow-scrolling: touch;
}

.auto-slider-row-cards {
  display: flex;
  gap: 20px;
  justify-content: center;
}
.testimonial-card {
  background-color: #F2F2F6;
  border-radius: 20px;
  padding: 20px;
  font-family: 'Nunito-Regular';
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 100%;
  max-width: 360px;
  min-width: 300px;

  @media (orientation: portrait) {
    min-width: 300px;
    max-width: 300px;
  }
}
.auto-slider-row-cards {
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-inline: 20px;
  @media (min-width: 1024px) {
    flex-wrap: nowrap;
  }
  @media (width: 1024px) {
    flex-wrap: wrap;
  }
}

.auto-slider-container-cards .card-content {
  @media (min-width: 1024px) {
    flex: 1 1 calc(50% - 20px);
    max-width: calc(50% - 20px);
    margin-bottom: 20px;
  }
}
</style>
